import React, { useState, useRef } from 'react';
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { IoSearchOutline, IoChevronBack, IoChevronForward, IoChevronDown, IoChevronForward as IoChevronRight } from 'react-icons/io5';

function GlobalFilter({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) {
  const count = preGlobalFilteredRows.length;

  return (
    <div className="d-flex align-items-center mb-4">
      <div className="position-relative w-100" style={{ maxWidth: 380 }}>
        <IoSearchOutline
          size={18}
          style={{
            position: 'absolute',
            left: 14,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            zIndex: 1,
          }}
        />
        <input
          type="text"
          className="form-control"
          value={globalFilter || ''}
          onChange={e => setGlobalFilter(e.target.value || undefined)}
          placeholder={`Search ${count} records...`}
          style={{ paddingLeft: 40 }}
        />
      </div>
    </div>
  );
}

const AdminTable = ({ tableHeaders = [], tableData = [], renderExpandedRow, getRowId }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const expandedRef = useRef(expandedRows);
  expandedRef.current = expandedRows;

  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const resolveRowId = (row) => {
    if (typeof getRowId === 'function') return getRowId(row);
    if (typeof getRowId === 'string') return row.original?.[getRowId];
    return row.id || row.index;
  };

  const data = React.useMemo(
    () => (tableData.length ? tableData : []),
    [tableData]
  );

  const hasExpand = typeof renderExpandedRow === 'function';

  const columns = React.useMemo(
    () => {
      const cols = tableHeaders.length ? [...tableHeaders] : [];
      if (hasExpand) {
        cols.unshift({
          Header: '',
          accessor: '_expand',
          disableSortBy: true,
          Cell: ({ row }) => {
            const rowId = resolveRowId(row);
            const isExpanded = expandedRef.current[rowId];
            return (
              <button
                onClick={() => toggleRow(rowId)}
                style={{ background: 'none', border: 'none', padding: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                type="button"
              >
                {isExpanded ? <IoChevronDown size={18} /> : <IoChevronRight size={18} />}
              </button>
            );
          },
          width: 40,
        });
      }
      return cols;
    },
    [tableHeaders, hasExpand]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,

    prepareRow,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
  } = useTable(
    { columns, data, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="mt-4">
      <GlobalFilter
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />

      <div className="table-responsive">
        <table {...getTableProps()} className="table table-hover">
          <thead>
            {headerGroups.map((headerGroup, index) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, colIndex) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={colIndex}
                    className="text-center"
                  >
                    {column.render('Header')}
                    <span style={{ marginLeft: 4, fontSize: 10 }}>
                      {column.isSorted
                        ? column.isSortedDesc
                          ? '▼'
                          : '▲'
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              const rowId = resolveRowId(row);
              const isExpanded = expandedRows[rowId];
              return (
                <React.Fragment key={rowId || i}>
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell, cellIndex) => (
                      <td
                        {...cell.getCellProps()}
                        key={cellIndex}
                        className="text-center"
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && renderExpandedRow && (
                    <tr className="expanded-row">
                      <td colSpan={columns.length} style={{ padding: 0, borderBottom: '2px solid #e9ecef' }}>
                        <div style={{ padding: '16px 24px', background: '#f8f9fa' }}>
                          {renderExpandedRow(row.original, () => toggleRow(rowId))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {page.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center py-5 text-muted">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn d-inline-flex align-items-center gap-1 previousbtn"
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            style={{ padding: '8px 16px' }}
          >
            <IoChevronBack size={14} />
            Previous
          </button>
          <button
            className="btn d-inline-flex align-items-center gap-1 nextbtn ml-2"
            onClick={() => nextPage()}
            disabled={!canNextPage}
            style={{ padding: '8px 16px' }}
          >
            Next
            <IoChevronForward size={14} />
          </button>
        </div>

        <span className="text-muted" style={{ fontSize: 13 }}>
          Page{' '}
          <strong>
            {state.pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>

        <div>
          <select
            className="form-control page-size-select"
            value={state.pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[5, 10, 20].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default AdminTable;
